/**
 * Shadow Field Helper
 * Handles shadow field value synchronization
 */

export class ShadowFieldHelper {
  static shadowFields = new Map(); // shadowFieldId -> sourceFieldIds[]
  static isSyncing = new Set(); // shadowFieldIds currently being synced (prevent loops)

  /**
   * Setup shadow field sync
   * @param {string} shadowFieldId - Shadow field ID
   * @param {Array} sourceFieldIds - Source field IDs
   */
  static setup(shadowFieldId, sourceFieldIds) {
    if (!sourceFieldIds || !Array.isArray(sourceFieldIds) || sourceFieldIds.length === 0) {
      return;
    }

    // Store configuration for event delegation
    this.shadowFields.set(shadowFieldId, sourceFieldIds);

    // Initial sync
    this.syncShadowField(shadowFieldId);

    // Setup event delegation if not already set up
    if (!this.delegationSetup) {
      this.setupEventDelegation();
      this.delegationSetup = true;
    }
  }

  /**
   * Setup event delegation for all shadow fields
   */
  static setupEventDelegation() {
    const handler = (e) => {
      const fieldName = e.target.name || e.target.id;
      if (!fieldName) return;
      const fieldId = fieldName.replace(/\[\]$/, '');

      this.shadowFields.forEach((sourceFieldIds, shadowFieldId) => {
        if (this.isSyncing.has(shadowFieldId)) return;
        if (sourceFieldIds.includes(fieldId) || sourceFieldIds.includes(fieldName)) {
          this.syncShadowField(shadowFieldId);
        }
      });
    };

    document.addEventListener('input', handler, true);
    document.addEventListener('change', handler, true);
  }

  /**
   * Get current value from a form field (input, select, textarea, radio, checkbox)
   * @param {string} sourceFieldId - Source field ID
   * @returns {string}
   */
  static getFieldValue(sourceFieldId) {
    const selector = `[name="${sourceFieldId}"], [name="${sourceFieldId}[]"], [id="${sourceFieldId}"]`;
    const field = document.querySelector(selector);
    if (!field) return '';

    if (field.type === 'radio') {
      const checked = document.querySelector(`[name="${sourceFieldId}"]:checked, [name="${sourceFieldId}[]"]:checked`);
      return checked ? (checked.value || '').trim() : '';
    }

    if (field.type === 'checkbox') {
      const checkedBoxes = document.querySelectorAll(`[name="${sourceFieldId}[]"]:checked, [name="${sourceFieldId}"]:checked`);
      const values = Array.from(checkedBoxes).map(cb => (cb.value || '').trim()).filter(Boolean);
      return values.join(', ');
    }

    return (field.value || '').trim();
  }

  /**
   * Sync shadow field value
   * @param {string} shadowFieldId - Shadow field ID
   */
  static syncShadowField(shadowFieldId) {
    const sourceFieldIds = this.shadowFields.get(shadowFieldId);
    if (!sourceFieldIds || sourceFieldIds.length === 0) {
      return;
    }

    const shadowField = document.querySelector(`[name="${shadowFieldId}"]`);
    if (!shadowField) return;

    if (this.isSyncing.has(shadowFieldId)) return;
    this.isSyncing.add(shadowFieldId);

    try {
      let concatenatedValue = '';
      sourceFieldIds.forEach(sourceFieldId => {
        concatenatedValue += this.getFieldValue(sourceFieldId);
      });
      shadowField.value = concatenatedValue;
      shadowField.dispatchEvent(new Event('input', { bubbles: true }));
    } finally {
      setTimeout(() => this.isSyncing.delete(shadowFieldId), 0);
    }
  }
}
